/**
 * Undo/Redo stack for checklist structural operations.
 *
 * Each entry describes how to reverse an operation:
 * - updates: items to patch (restore old field values)
 * - additions: items to re-add (restore deleted items)
 * - removals: item IDs to delete (remove created items)
 * - cursor: where to place the cursor after undo/redo
 */

import type { DbObject } from "@/db/schema"

export interface UndoCursor {
  id: string
  offset: number
  atEnd?: boolean
}

export interface UndoEntry {
  /** Items to update (restore old values) */
  updates: Array<{ id: string; data: Partial<DbObject> }>
  /** Items to re-create (restore deleted) */
  additions: DbObject[]
  /** Item IDs to remove (undo creations) */
  removals: string[]
  /** Cursor position to restore */
  cursor: UndoCursor
}

const MAX_UNDO = 50

export class ChecklistUndoStack {
  private undoStack: UndoEntry[] = []
  private redoStack: UndoEntry[] = []

  /** Push a new operation (clears redo stack) */
  push(entry: UndoEntry) {
    this.undoStack.push(entry)
    if (this.undoStack.length > MAX_UNDO) {
      this.undoStack.shift()
    }
    // New operation invalidates redo
    this.redoStack = []
  }

  /** Push to undo without clearing redo (used internally by redo) */
  pushUndo(entry: UndoEntry) {
    this.undoStack.push(entry)
    if (this.undoStack.length > MAX_UNDO) {
      this.undoStack.shift()
    }
  }

  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  popUndo(): UndoEntry | undefined {
    return this.undoStack.pop()
  }

  pushRedo(entry: UndoEntry) {
    this.redoStack.push(entry)
  }

  popRedo(): UndoEntry | undefined {
    return this.redoStack.pop()
  }

  clear() {
    this.undoStack = []
    this.redoStack = []
  }
}
